import api from '../api';
import toastService from './toastService';

export interface FAQCategory {
  id: number;
  tenant_id: number;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  display_order: number;
  is_active: boolean;
  faq_count?: number;
  created_at: string;
  updated_at: string;
}

export interface FAQ {
  id: number;
  tenant_id: number;
  category_id?: number;
  question: string;
  answer: string;
  source_reference?: string;
  tags: string[];
  is_published: boolean;
  view_count: number;
  helpful_count: number;
  not_helpful_count: number;
  search_keywords: string[];
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  category?: FAQCategory;
  creator?: {
    id: number;
    name: string;
    email?: string;
  };
}

export interface FAQListResponse {
  faqs: FAQ[];
  total: number;
  page: number;
  pages: number;
}

export interface BulkUploadResult {
  total: number;
  created: number;
  duplicates: number;
  failed: number;
  categoriesCreated: number;
  errors: Array<{
    row?: number;
    question?: string;
    error: string;
  }>;
}

export interface AnalyticsSummary {
  topViewed: FAQ[];
  topHelpful: FAQ[];
  categoryStats: Array<{
    id: number;
    name: string;
    faq_count: number;
  }>;
  searchAnalytics: Array<{
    search_query: string;
    count: number;
  }>;
}

export interface FAQChatResponse {
  answer: string;
  suggestedFAQs: Array<{
    id: number;
    question: string;
    answer: string;
    source_reference?: string;
    similarity_score: number;
  }>;
  sources: string[];
}

class FAQAPI {
  async getCategories(): Promise<FAQCategory[]> {
    try {
      const response = await api.get('/api/faq/categories');
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to fetch categories';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async createCategory(data: {
    name: string;
    slug?: string;
    description?: string;
    icon?: string;
    display_order?: number;
  }): Promise<FAQCategory> {
    try {
      const response = await api.post('/api/faq/categories', data);
      toastService.success('Category created successfully');
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to create category';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async getFAQs(params?: {
    page?: number;
    limit?: number;
    category_id?: number;
    is_published?: boolean;
  }): Promise<FAQListResponse> {
    try {
      const response = await api.get('/api/faq', { params });
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to fetch FAQs';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async searchFAQs(query: string, categoryId?: number, limit?: number): Promise<FAQ[]> {
    try {
      const params: any = { q: query };
      if (categoryId) params.category_id = categoryId;
      if (limit) params.limit = limit;

      const response = await api.get('/api/faq/search', { params });
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Search failed';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async getFAQById(id: number): Promise<FAQ> {
    try {
      const response = await api.get(`/api/faq/${id}`);
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to fetch FAQ';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async createFAQ(data: {
    category_id?: number;
    question: string;
    answer: string;
    source_reference?: string;
    tags?: string[];
    is_published?: boolean;
  }): Promise<FAQ> {
    try {
      const response = await api.post('/api/faq', data);
      toastService.success('FAQ created successfully');
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to create FAQ';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async updateFAQ(
    id: number,
    data: Partial<{
      category_id?: number;
      question: string;
      answer: string;
      source_reference?: string;
      tags?: string[];
      is_published?: boolean;
    }>
  ): Promise<FAQ> {
    try {
      const response = await api.put(`/api/faq/${id}`, data);
      toastService.success('FAQ updated successfully');
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to update FAQ';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async deleteFAQ(id: number): Promise<void> {
    try {
      await api.delete(`/api/faq/${id}`);
      toastService.success('FAQ deleted successfully');
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to delete FAQ';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async bulkUploadFAQs(file: File): Promise<BulkUploadResult> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/faq/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const result = response.data;

      if (result.created > 0) {
        let message = `✓ Successfully uploaded ${result.created} FAQ${result.created !== 1 ? 's' : ''}`;
        if (result.categoriesCreated > 0) {
          message += ` and created ${result.categoriesCreated} new categor${result.categoriesCreated !== 1 ? 'ies' : 'y'}`;
        }
        toastService.success(message);
      }

      if (result.duplicates > 0) {
        toastService.warning(`⚠ Found ${result.duplicates} duplicate question${result.duplicates !== 1 ? 's' : ''}`);
      }

      if (result.failed > 0) {
        toastService.error(`✗ ${result.failed} FAQ${result.failed !== 1 ? 's' : ''} failed to upload`);
      }

      return result;
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Bulk upload failed';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async trackView(id: number): Promise<void> {
    try {
      await api.get(`/api/faq/${id}`);
    } catch (error: any) {
      console.error('Failed to track view:', error);
    }
  }

  async markHelpful(id: number, helpful: boolean): Promise<{ success: boolean; changed: boolean }> {
    try {
      const response = await api.post(`/api/faq/${id}/helpful`, { helpful });
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to mark feedback';
      throw error;
    }
  }

  async getAnalytics(startDate?: string, endDate?: string): Promise<AnalyticsSummary> {
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get('/api/faq/analytics', { params });
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to fetch analytics';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async askFAQChat(question: string, modelId?: string): Promise<FAQChatResponse> {
    try {
      const response = await api.post('/api/faq-chat/ask', {
        question,
        model_id: modelId
      });
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to get answer';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async getSuggestions(query: string): Promise<Array<{ id: number; question: string; category: string | null }>> {
    try {
      const response = await api.get('/api/faq-chat/suggestions', {
        params: { query }
      });
      return response.data;
    } catch (error: any) {
      return [];
    }
  }
}

export default new FAQAPI();
